/**
 * Team Manager (TM) types — /tm module
 */

// ─── Project ──────────────────────────────────────────────────────────────────

export type ProjectStatus = 'active' | 'completed' | 'archived';

export interface TMProject {
  id:          string;
  name:        string;
  description: string | null;
  color:       string;         // key from PROJECT_COLORS
  status:      ProjectStatus;
  memberUids:  string[];       // UIDs of approved TM users who can access this project
  createdBy:   TMUserRef;
  createdAt:   Date | null;
  updatedAt:   Date | null;
}

export const PROJECT_COLORS: { key: string; bg: string; light: string; text: string; dot: string }[] = [
  { key: 'indigo',  bg: 'bg-indigo-500',  light: 'bg-indigo-50',  text: 'text-indigo-600',  dot: '#6366f1' },
  { key: 'emerald', bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', dot: '#10b981' },
  { key: 'amber',   bg: 'bg-amber-500',   light: 'bg-amber-50',   text: 'text-amber-600',   dot: '#f59e0b' },
  { key: 'rose',    bg: 'bg-rose-500',    light: 'bg-rose-50',    text: 'text-rose-600',    dot: '#f43f5e' },
  { key: 'blue',    bg: 'bg-blue-500',    light: 'bg-blue-50',    text: 'text-blue-600',    dot: '#3b82f6' },
  { key: 'purple',  bg: 'bg-purple-500',  light: 'bg-purple-50',  text: 'text-purple-600',  dot: '#a855f7' },
  { key: 'teal',    bg: 'bg-teal-500',    light: 'bg-teal-50',    text: 'text-teal-600',    dot: '#14b8a6' },
  { key: 'orange',  bg: 'bg-orange-500',  light: 'bg-orange-50',  text: 'text-orange-600',  dot: '#f97316' },
];

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  active:    'Active',
  completed: 'Completed',
  archived:  'Archived',
};

// ─── TM User ──────────────────────────────────────────────────────────────────

export type TMUserStatus = 'pending' | 'approved' | 'rejected' | 'deactivated';
export type TMUserRole   = 'admin' | 'member';

export interface TMUserPermissions {
  createTask:   boolean;
  editTask:     boolean;
  updateTask:   boolean;   // update status / progress
  assignTask:   boolean;
  approveUsers: boolean;
}

export const DEFAULT_TM_PERMISSIONS: TMUserPermissions = {
  createTask:   false,
  editTask:     false,
  updateTask:   true,    // members can always update their own task progress
  assignTask:   false,
  approveUsers: false,
};

export interface TMUser {
  uid:         string;
  name:        string;
  email:       string;
  phone:       string;
  status:      TMUserStatus;
  role:        TMUserRole;
  permissions: TMUserPermissions;
  avatarUrl:   string | null;
  createdAt:   Date | null;
  approvedAt:  Date | null;
  approvedBy:  string | null;
}

export interface TMUserRef {
  uid:   string;
  name:  string;
  email: string;
  phone?: string;
}

// ─── Task ─────────────────────────────────────────────────────────────────────

export type TaskStatus   = 'todo' | 'in_progress' | 'review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Task {
  id:           string;
  projectId:    string | null;   // null = general task, not in a project
  title:        string;
  description:  string | null;
  assignedTo:   TMUserRef | null;
  assignedBy:   TMUserRef;
  status:       TaskStatus;
  priority:     TaskPriority;
  dueDate:      Date | null;
  reminderDays: number;
  progress:     number;
  tags:         string[];
  createdAt:    Date | null;
  updatedAt:    Date | null;
}

// ─── Task History ─────────────────────────────────────────────────────────────

export type TaskHistoryAction =
  | 'created'
  | 'status_changed'
  | 'progress_changed'
  | 'assigned'
  | 'reassigned'
  | 'priority_changed'
  | 'due_date_changed'
  | 'edited'
  | 'comment';

export interface TaskHistoryChange {
  field:    string;
  oldValue: string | null;
  newValue: string | null;
}

export interface TaskHistoryEntry {
  id:        string;
  userId:    string;
  userName:  string;
  action:    TaskHistoryAction;
  comment:   string | null;
  changes:   TaskHistoryChange[];
  createdAt: Date | null;
}

export const TASK_HISTORY_LABELS: Record<TaskHistoryAction, string> = {
  created:          'Task created',
  status_changed:   'Status changed',
  progress_changed: 'Progress updated',
  assigned:         'Task assigned',
  reassigned:       'Task reassigned',
  priority_changed: 'Priority changed',
  due_date_changed: 'Due date changed',
  edited:           'Task edited',
  comment:          'Comment added',
};

// ─── Labels & colours ─────────────────────────────────────────────────────────

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  todo:        'To Do',
  in_progress: 'In Progress',
  review:      'Review',
  done:        'Done',
};

export const TASK_STATUS_COLORS: Record<TaskStatus, string> = {
  todo:        'bg-surface-100 text-surface-600',
  in_progress: 'bg-amber-100 text-amber-700',
  review:      'bg-blue-100 text-blue-700',
  done:        'bg-emerald-100 text-emerald-700',
};

export const TASK_STATUS_DOT: Record<TaskStatus, string> = {
  todo:        'bg-surface-400',
  in_progress: 'bg-amber-400',
  review:      'bg-blue-400',
  done:        'bg-emerald-400',
};

export const TASK_PRIORITY_LABELS: Record<TaskPriority, string> = {
  low:    'Low',
  medium: 'Medium',
  high:   'High',
  urgent: 'Urgent',
};

export const TASK_PRIORITY_COLORS: Record<TaskPriority, string> = {
  low:    'text-surface-500 bg-surface-100',
  medium: 'text-blue-600 bg-blue-50',
  high:   'text-amber-600 bg-amber-50',
  urgent: 'text-red-600 bg-red-50',
};

export const TASK_STATUSES:   TaskStatus[]   = ['todo', 'in_progress', 'review', 'done'];
export const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
